import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Avatar from '../components/Avatar';
import { useAuth } from '../AuthContext';
import { useRealtimeEvents, useRealtimeContext } from '../RealtimeContext';
import { ContextMenu, MenuItem, MenuDivider, Icons } from '../dm/Menu';
import { BlockDialog, DeleteChatDialog, LeaveGroupDialog } from '../dm/ConfirmDialogs';
import GroupAvatar from '../dm/GroupAvatar';
import { attachmentLabel } from '../dm/Attachments';
import { dmApi, chatName, displayName, formatListTime, senderOf, systemText } from '../dm/format';
import { Collapse, Motion, presets, useFlip } from '../motion';

const MAX_PINNED = 3;
const PINNED_KEY = 'pinned_conversations';

function getPinnedIds() {
  try { return JSON.parse(localStorage.getItem(PINNED_KEY) || '[]'); } catch { return []; }
}
function savePinnedIds(ids) {
  try { localStorage.setItem(PINNED_KEY, JSON.stringify(ids)); } catch {}
}

// Before Phase 3, pinned chats and the last opened chat were stored as partner user ids.
// Translate them once to conversation ids, then drop the old keys.
function migrateStoredChatIds(conversations) {
  try {
    const byPartner = new Map(conversations.filter(c => c.partner).map(c => [c.partner.id, c.id]));
    const oldPinned = localStorage.getItem('pinned_chats');
    if (oldPinned !== null) {
      const ids = JSON.parse(oldPinned || '[]').map(id => byPartner.get(id)).filter(Boolean);
      if (!localStorage.getItem(PINNED_KEY)) localStorage.setItem(PINNED_KEY, JSON.stringify(ids.slice(0, MAX_PINNED)));
      localStorage.removeItem('pinned_chats');
    }
    const oldLast = localStorage.getItem('last_chat');
    if (oldLast !== null) {
      const id = byPartner.get(parseInt(oldLast, 10));
      if (id && !localStorage.getItem('last_conversation')) localStorage.setItem('last_conversation', String(id));
      localStorage.removeItem('last_chat');
    }
  } catch {}
}

export default function ConversationList() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeId = parseInt(location.pathname.match(/^\/dm\/(\d+)$/)?.[1], 10);
  const { user, nicknames } = useAuth();
  const { presence } = useRealtimeContext();
  const [conversations, setConversations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequests, setShowRequests] = useState(false);
  const [pinned, setPinned] = useState(getPinnedIds);
  const [menu, setMenu] = useState(null); // { conv, x, y }
  const [confirm, setConfirm] = useState(null); // { type: 'delete' | 'block', conv }
  const [busy, setBusy] = useState(false);
  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);
  const migrated = useRef(false);

  const load = useCallback(async () => {
    try {
      const [convRes, reqRes] = await Promise.all([
        dmApi.get('/api/dm/conversations'),
        dmApi.get('/api/dm/requests'),
      ]);
      if (!migrated.current) {
        migrated.current = true;
        migrateStoredChatIds(convRes.data);
        setPinned(getPinnedIds());
      }
      setConversations(convRes.data);
      setRequests(reqRes.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  // Bursts of realtime events trigger one reload
  const reloadTimer = useRef(null);
  const scheduleLoad = () => {
    clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(load, 150);
  };
  useEffect(() => { load(); return () => clearTimeout(reloadTimer.current); }, [load]);
  useRealtimeEvents(
    ['dm:message', 'dm:edited', 'dm:deleted', 'dm:read', 'dm:request', 'dm:conversation_deleted', 'dm:conversation_updated', 'dm:block', 'resync'],
    scheduleLoad
  );
  // Opening a chat marks it read; refresh the unread badge
  useEffect(() => {
    const onRead = () => scheduleLoad();
    window.addEventListener('dm-read', onRead);
    return () => window.removeEventListener('dm-read', onRead);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startLongPress = (conv, e) => {
    longPressTriggered.current = false;
    const touch = e.touches?.[0];
    const x = touch?.clientX ?? e.clientX;
    const y = touch?.clientY ?? e.clientY;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      setMenu({ conv, x, y });
    }, 500);
  };
  const cancelLongPress = () => clearTimeout(longPressTimer.current);

  const open = (conv) => {
    if (longPressTriggered.current) return;
    navigate(`/dm/${conv.id}`);
  };

  const togglePin = (id) => {
    setPinned(prev => {
      let next;
      if (prev.includes(id)) next = prev.filter(x => x !== id);
      else if (prev.length >= MAX_PINNED) return prev;
      else next = [id, ...prev];
      savePinnedIds(next);
      return next;
    });
    setMenu(null);
  };

  const deleteChat = async (conv) => {
    setBusy(true);
    try {
      await dmApi.delete(`/api/dm/conversations/${conv.id}`);
      setConversations(prev => prev.filter(c => c.id !== conv.id));
      setPinned(prev => { const next = prev.filter(id => id !== conv.id); savePinnedIds(next); return next; });
      if (activeId === conv.id) navigate('/dm');
    } catch {}
    setBusy(false);
    setConfirm(null);
  };

  const leaveGroup = async (conv) => {
    setBusy(true);
    try {
      await dmApi.delete(`/api/dm/groups/${conv.id}/members/${user.id}`);
      setConversations(prev => prev.filter(c => c.id !== conv.id));
      if (activeId === conv.id) navigate('/dm');
    } catch {}
    setBusy(false);
    setConfirm(null);
  };

  const setBlocked = async (conv, blocked) => {
    setBusy(true);
    try {
      if (blocked) await dmApi.post(`/api/users/${conv.partner.id}/block`);
      else await dmApi.delete(`/api/users/${conv.partner.id}/block`);
      load();
    } catch {}
    setBusy(false);
    setConfirm(null);
  };

  const sorted = [
    ...pinned.map(id => conversations.find(c => c.id === id)).filter(Boolean),
    ...conversations.filter(c => !pinned.includes(c.id)),
  ];
  const nameOf = (conv) => chatName(conv, { nicknames, meId: user?.id });
  const requestName = (req) => nicknames[req.sender_id] || req.display_name || req.username;
  const previewOf = (conv) => {
    const m = conv.last_message;
    if (!m) return 'No messages yet';
    if (m.kind === 'system') return systemText(m, { meId: user?.id, nicknames });
    // Groups name the sender of the last message, 1:1 chats only mark own messages
    const prefix = m.sender_id === user?.id ? 'You: '
      : conv.type === 'group' ? `${displayName(senderOf(m), nicknames)}: ` : '';
    return prefix + (m.content || (m.attachment ? attachmentLabel(m.attachment) : ''));
  };

  // Chats glide to their new place when the order changes (new message, pinning)
  const listRef = useRef();
  useFlip(listRef, [sorted.map(c => c.id).join(','), requests.length > 0, showRequests]);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="h-6 w-6 animate-spin rounded-full border-[3px] border-solid border-border border-t-purple" />
      </div>
    );
  }

  return (
    <div ref={listRef} className="relative flex flex-col gap-0.5 px-2 pb-2">
      {/* Message requests */}
      {requests.length > 0 && (
        <div className="mb-1">
          <button
            type="button"
            onClick={() => setShowRequests(v => !v)}
            className="flex w-full cursor-pointer items-center gap-3 rounded border-0 bg-purple-glow px-2 py-2 text-left font-body text-text hover:bg-surface-2"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber text-sm font-extrabold text-black">
              {requests.length > 9 ? '9+' : requests.length}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-display text-sm font-extrabold">Message requests</span>
              <span className="block truncate text-xs text-text-dim">
                {requests.length === 1 ? requestName(requests[0]) : `${requestName(requests[0])} and ${requests.length - 1} more`}
              </span>
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`shrink-0 text-text-dim transition-transform ${showRequests ? 'rotate-180' : ''}`}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <Collapse open={showRequests}>
            <div className="flex flex-col gap-0.5 pt-0.5">
              {requests.map(req => (
                <RequestRow key={req.sender_id} req={req} name={requestName(req)} active={req.conversation_id === activeId} onAction={load} />
              ))}
            </div>
          </Collapse>
        </div>
      )}

      {sorted.length === 0 && requests.length === 0 && (
        <div className="px-3 py-10 text-center">
          <div className="mb-2 font-display text-base font-extrabold text-text">No chats yet</div>
          <div className="text-sm leading-relaxed text-text-dim">Search for someone above to start a conversation.</div>
        </div>
      )}

      {sorted.map(conv => {
        const isPinned = pinned.includes(conv.id);
        const isPending = conv.request_status === 'pending';
        const isActive = conv.id === activeId;
        const unread = !isPending && conv.unread_count > 0;
        return (
          <div
            key={conv.id}
            data-flip-key={conv.id}
            role="button"
            tabIndex={0}
            onClick={() => open(conv)}
            onKeyDown={e => { if (e.key === 'Enter') open(conv); }}
            onContextMenu={e => { e.preventDefault(); setMenu({ conv, x: e.clientX, y: e.clientY }); }}
            onTouchStart={e => startLongPress(conv, e)}
            onTouchEnd={cancelLongPress}
            onTouchMove={cancelLongPress}
            className={`flex cursor-pointer select-none items-center gap-3 rounded px-2 py-2 transition-colors ${
              isActive ? 'bg-surface-3' : 'hover:bg-surface-2'
            } ${isPending || conv.blocked_by_me ? 'opacity-75' : ''}`}
          >
            {conv.type === 'group'
              ? <GroupAvatar icon={conv.icon} members={conv.members} size={36} />
              : <Avatar src={conv.partner?.avatar} size={36} status={conv.blocked_by_me ? undefined : presence[conv.partner?.id]} />}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <span className={`truncate font-display text-[15px] ${unread || isActive ? 'font-extrabold text-text' : 'font-bold text-text-muted'}`}>
                  {nameOf(conv)}
                </span>
                {isPinned && <span className="flex shrink-0 text-purple-light" aria-label="Pinned" style={{ transform: 'scale(0.7)' }}>{Icons.pin}</span>}
                <span className="ml-auto shrink-0 text-[11px] text-text-dim">{formatListTime(conv.last_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                {isPending ? (
                  <span className="text-xs font-bold text-purple-light">Request pending…</span>
                ) : conv.blocked_by_me ? (
                  <span className="text-xs text-text-dim">Blocked</span>
                ) : (
                  <span className={`flex-1 truncate text-[13px] ${unread ? 'font-semibold text-text' : 'text-text-dim'}`}>{previewOf(conv)}</span>
                )}
                {unread && (
                  <Motion as="span" {...presets.pop} className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-purple px-1 text-[11px] font-extrabold text-white">
                    {conv.unread_count > 9 ? '9+' : conv.unread_count}
                  </Motion>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {menu && (
        <ContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          <MenuItem
            icon={Icons.pin}
            onClick={() => togglePin(menu.conv.id)}
            disabled={!pinned.includes(menu.conv.id) && pinned.length >= MAX_PINNED}
          >
            {pinned.includes(menu.conv.id) ? 'Unpin' : `Pin${pinned.length >= MAX_PINNED ? ` (max ${MAX_PINNED})` : ''}`}
          </MenuItem>
          {menu.conv.partner && (
            menu.conv.blocked_by_me ? (
              <MenuItem icon={Icons.block} onClick={() => { setMenu(null); setBlocked(menu.conv, false); }}>Unblock</MenuItem>
            ) : (
              <MenuItem icon={Icons.block} danger onClick={() => { setConfirm({ type: 'block', conv: menu.conv }); setMenu(null); }}>Block</MenuItem>
            )
          )}
          {menu.conv.type === 'dm' && <>
            <MenuDivider />
            <MenuItem icon={Icons.trash} danger onClick={() => { setConfirm({ type: 'delete', conv: menu.conv }); setMenu(null); }}>Delete chat</MenuItem>
          </>}
          {menu.conv.type === 'group' && <>
            <MenuDivider />
            <MenuItem icon={Icons.logout} danger onClick={() => { setConfirm({ type: 'leave', conv: menu.conv }); setMenu(null); }}>Leave group</MenuItem>
          </>}
        </ContextMenu>
      )}

      {confirm?.type === 'delete' && (
        <DeleteChatDialog name={nameOf(confirm.conv)} busy={busy} onConfirm={() => deleteChat(confirm.conv)} onClose={() => setConfirm(null)} />
      )}
      {confirm?.type === 'leave' && (
        <LeaveGroupDialog name={nameOf(confirm.conv)} busy={busy} onConfirm={() => leaveGroup(confirm.conv)} onClose={() => setConfirm(null)} />
      )}
      {confirm?.type === 'block' && (
        <BlockDialog name={nameOf(confirm.conv)} busy={busy} onConfirm={() => setBlocked(confirm.conv, true)} onClose={() => setConfirm(null)} />
      )}
    </div>
  );
}

function RequestRow({ req, name, active, onAction }) {
  const navigate = useNavigate();
  const [acting, setActing] = useState(false);

  const act = async (e, action) => {
    e.stopPropagation();
    setActing(true);
    try {
      await dmApi.post(`/api/dm/request/${req.sender_id}/${action}`);
      window.dispatchEvent(new Event('notif-read'));
      onAction();
      if (action === 'accept' && req.conversation_id) navigate(`/dm/${req.conversation_id}`);
    } catch {} finally { setActing(false); }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(req.conversation_id ? `/dm/${req.conversation_id}` : `/dm/user/${req.sender_id}`)}
      className={`ml-2 flex cursor-pointer items-center gap-2 rounded px-2 py-2 hover:bg-surface-2 ${active ? 'bg-surface-3' : ''}`}
    >
      <Avatar src={req.avatar} size={28} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-display text-sm font-bold text-text">{name}</div>
        <div className="truncate text-xs text-text-dim">{req.preview || `${req.message_count} message${req.message_count !== 1 ? 's' : ''}`}</div>
      </div>
      <button type="button" disabled={acting} onClick={e => act(e, 'decline')} title="Decline"
        className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-surface-3 text-text-muted hover:text-red">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
      </button>
      <button type="button" disabled={acting} onClick={e => act(e, 'accept')} title="Accept"
        className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-brand text-white">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      </button>
    </div>
  );
}
